import React, { useMemo, useState } from 'react';
import Card from '@/components/UI/Card';
import FormCheckbox from '@/components/UI/FormCheckbox';
import { FaInfoCircle } from 'react-icons/fa';
import { getTimeHorizonsForDuration, formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import type { AutoDiscoveryConfigValue } from '@/utils/experimentHorizonConfig';

export type { AutoDiscoveryConfigValue } from '@/utils/experimentHorizonConfig';

interface AutoDiscoveryConfigProps {
    availableDurations: string[];
    onChange: (config: AutoDiscoveryConfigValue | null) => void;
    value: AutoDiscoveryConfigValue | null;
}

const buildConfigFromSelections = (selectedCombinations: ReadonlySet<string>): AutoDiscoveryConfigValue | null => {
    if (selectedCombinations.size === 0) return null;

    return {
        byDuration: Object.fromEntries(
            Object.entries(
                Array.from(selectedCombinations).reduce<Record<string, number[]>>((config, combination) => {
                    const [duration, horizonValue] = combination.split('-');
                    const horizon = Number.parseInt(horizonValue, 10);

                    if (!config[duration]) {
                        config[duration] = [];
                    }

                    config[duration].push(horizon);
                    return config;
                }, {})
            ).map(([duration, horizons]) => [duration, horizons.sort((left, right) => left - right)])
        )
    };
};

const AutoDiscoveryConfig: React.FC<AutoDiscoveryConfigProps> = ({ availableDurations, onChange, value }) => {
    // A null persisted value means disabled, except while the user has enabled the grid but has not
    // selected a pair yet. Persisted selections otherwise remain controlled by the parent form.
    const [enabledWithoutSelections, setEnabledWithoutSelections] = useState(false);

    const selectedCombinations = useMemo(() => {
        const selections = new Set<string>();

        Object.entries(value?.byDuration ?? {}).forEach(([duration, horizons]) => {
            horizons.forEach((horizon) => selections.add(`${duration}-${horizon}`));
        });

        return selections;
    }, [value]);

    const enabled = value !== null || enabledWithoutSelections;

    // Get horizons for each duration
    const horizonsByDuration = useMemo(() => {
        const map = new Map<string, number[]>();

        availableDurations.forEach((duration) => {
            const horizons = getTimeHorizonsForDuration(duration);
            map.set(duration, horizons);
        });

        return map;
    }, [availableDurations]);

    // Get all unique horizons across all durations (for column headers)
    const allUniqueHorizons = useMemo(() => {
        const horizonSet = new Set<number>();

        horizonsByDuration.forEach((horizons) => {
            horizons.forEach((h) => horizonSet.add(h));
        });

        return Array.from(horizonSet).sort((a, b) => a - b);
    }, [horizonsByDuration]);

    const commitSelections = (nextSelections: Set<string>) => {
        setEnabledWithoutSelections(true);
        onChange(buildConfigFromSelections(nextSelections));
    };

    const handleToggleEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;

        if (!checked) {
            setEnabledWithoutSelections(false);
            onChange(null);
            return;
        }

        setEnabledWithoutSelections(true);
    };

    const handleToggleCombination = (duration: string, horizon: number) => {
        const key = `${duration}-${horizon}`;
        const newSet = new Set(selectedCombinations);

        if (newSet.has(key)) {
            newSet.delete(key);
        } else {
            newSet.add(key);
        }

        commitSelections(newSet);
    };

    const handleSelectAllForDuration = (duration: string) => {
        const newSet = new Set(selectedCombinations);
        const horizonsForDuration = horizonsByDuration.get(duration) || [];

        const allSelected = horizonsForDuration.every((h) => selectedCombinations.has(`${duration}-${h}`));

        horizonsForDuration.forEach((h) => {
            const key = `${duration}-${h}`;

            if (allSelected) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
        });

        commitSelections(newSet);
    };

    const handleSelectAllForHorizon = (horizon: number) => {
        const newSet = new Set(selectedCombinations);

        // Only select for durations that actually have this horizon
        const applicableDurations = availableDurations.filter((d) => {
            const horizons = horizonsByDuration.get(d) || [];
            return horizons.includes(horizon);
        });

        const allSelected = applicableDurations.every((d) => selectedCombinations.has(`${d}-${horizon}`));

        applicableDurations.forEach((d) => {
            const key = `${d}-${horizon}`;

            if (allSelected) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
        });

        commitSelections(newSet);
    };

    const handleSelectAll = () => {
        let totalPossible = 0;

        availableDurations.forEach((d) => {
            const horizons = horizonsByDuration.get(d) || [];
            totalPossible += horizons.length;
        });

        const allSelected = selectedCombinations.size === totalPossible;

        if (allSelected) {
            commitSelections(new Set());
        } else {
            const newSet = new Set<string>();

            availableDurations.forEach((d) => {
                const horizons = horizonsByDuration.get(d) || [];

                horizons.forEach((h) => {
                    newSet.add(`${d}-${h}`);
                });
            });

            commitSelections(newSet);
        }
    };

    const selectedCount = selectedCombinations.size;

    const totalPossible = useMemo(() => {
        let total = 0;

        availableDurations.forEach((d) => {
            const horizons = horizonsByDuration.get(d) || [];
            total += horizons.length;
        });

        return total;
    }, [availableDurations, horizonsByDuration]);

    return (
        <Card className="bg-zinc-900 p-4">
            <div className="mb-4">
                <FormCheckbox
                    checked={enabled}
                    helpText="Automatically analyze parameter impacts after experiment completion"
                    label="Enable Automatic Parameter Discovery"
                    onChange={handleToggleEnabled}
                />
            </div>

            {enabled && (
                <>
                    <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-zinc-200">Select Duration/Horizon Combinations</h4>
                            <FaInfoCircle
                                className="text-zinc-500 text-xs"
                                title="Each duration has specific time horizons appropriate for its timeframe"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-zinc-400">
                                {selectedCount} of {totalPossible} selected
                            </span>
                            <button
                                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                                type="button"
                                onClick={handleSelectAll}
                            >
                                {selectedCount === totalPossible ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                    </div>

                    {availableDurations.length === 0 ? (
                        <div className="text-center py-4 text-zinc-500">No durations selected for this experiment</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr>
                                        <th className="text-left text-xs font-medium text-zinc-400 pb-2 pr-3">Duration</th>
                                        {allUniqueHorizons.map((horizon) => (
                                            <th
                                                className="text-center text-xs font-medium text-zinc-400 pb-2 px-2 min-w-[60px]"
                                                key={horizon}
                                            >
                                                <button
                                                    className="hover:text-zinc-200 transition-colors"
                                                    title={`Toggle all ${formatTimeHorizon(horizon)}`}
                                                    type="button"
                                                    onClick={() => handleSelectAllForHorizon(horizon)}
                                                >
                                                    {formatTimeHorizon(horizon)}
                                                </button>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {availableDurations.map((duration) => {
                                        const horizonsForDuration = horizonsByDuration.get(duration) || [];

                                        const selectedForDuration = horizonsForDuration.filter((h) =>
                                            selectedCombinations.has(`${duration}-${h}`)
                                        ).length;

                                        return (
                                            <tr className="border-t border-zinc-800" key={duration}>
                                                <td className="py-2 pr-3">
                                                    <button
                                                        className="text-sm font-medium text-zinc-300 hover:text-zinc-100 transition-colors flex items-center gap-2"
                                                        title={`Toggle all ${duration}`}
                                                        type="button"
                                                        onClick={() => handleSelectAllForDuration(duration)}
                                                    >
                                                        {duration}
                                                        <span className="text-xs text-zinc-500">
                                                            ({selectedForDuration}/{horizonsForDuration.length})
                                                        </span>
                                                    </button>
                                                </td>
                                                {allUniqueHorizons.map((horizon) => {
                                                    const isApplicable = horizonsForDuration.includes(horizon);
                                                    const key = `${duration}-${horizon}`;
                                                    const isSelected = selectedCombinations.has(key);

                                                    return (
                                                        <td className="text-center py-2 px-2" key={horizon}>
                                                            {isApplicable ? (
                                                                <button
                                                                    aria-label={`${duration} at ${formatTimeHorizon(horizon)}`}
                                                                    className={`
                                                                        w-5 h-5 rounded border-2 transition-all
                                                                        ${
                                                                            isSelected
                                                                                ? 'bg-blue-500 border-blue-500'
                                                                                : 'bg-transparent border-zinc-600 hover:border-zinc-400'
                                                                        }
                                                                    `}
                                                                    title={`${duration} at ${formatTimeHorizon(horizon)}`}
                                                                    type="button"
                                                                    onClick={() => handleToggleCombination(duration, horizon)}
                                                                >
                                                                    {isSelected && (
                                                                        <svg
                                                                            className="w-full h-full text-white"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            strokeWidth={3}
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path
                                                                                d="M5 13l4 4L19 7"
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                            />
                                                                        </svg>
                                                                    )}
                                                                </button>
                                                            ) : (
                                                                <span className="text-zinc-700 text-xs">–</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {enabled && selectedCount === 0 && availableDurations.length > 0 && (
                        <div className="mt-3 text-xs text-amber-400 flex items-center gap-1">
                            <FaInfoCircle />
                            <span>Please select at least one combination to enable auto-discovery</span>
                        </div>
                    )}

                    {enabled && selectedCount > 30 && (
                        <div className="mt-3 text-xs text-amber-400 flex items-center gap-1">
                            <FaInfoCircle />
                            <span>{selectedCount} combinations selected. Large numbers of combinations may take longer to process.</span>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
};

export default AutoDiscoveryConfig;
